import React from 'react';
import IconProvider from '../ui/IconProvider';

interface FieldValueRendererProps {
  fieldLabel: string;
  fieldValue: any;
  fieldType: string;
}

export const FieldValueRenderer: React.FC<FieldValueRendererProps> = ({
  fieldLabel,
  fieldValue,
  fieldType,
}) => {
  // Handle empty values
  if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
    return (
      <div className="flex items-start gap-2 text-xs">
        <span className="font-medium min-w-[80px] truncate text-muted-foreground">{fieldLabel}:</span>
        <span className="text-muted-foreground italic flex-1">Not set</span>
      </div>
    );
  }

  const renderValue = () => {
    switch (fieldType) {
      case 'boolean':
        const boolValue = typeof fieldValue === 'boolean' ? fieldValue : fieldValue === 'true' || fieldValue === true;
        return (
          <span className={boolValue ? 'text-green-600' : 'text-red-600'}>
            {boolValue ? 'True' : 'False'}
          </span>
        );

      case 'number':
        return <span className="text-blue-600 font-mono">{fieldValue}</span>;

      case 'url':
        if (fieldValue && typeof fieldValue === 'object' && fieldValue.url) {
          return (
            <a
              href={fieldValue.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 hover:text-purple-800 underline truncate inline-flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {fieldValue.text || fieldValue.url}
              <IconProvider iconstring="fa-external-link-alt" size={10} />
            </a>
          );
        }
        return <span className="text-muted-foreground truncate">{fieldValue}</span>;

      case 'title':
      case 'text':
      case 'textarea':
        // Handle long text with line clamp
        if (typeof fieldValue === 'string' && fieldValue.length > 100) {
          return <span className="text-muted-foreground line-clamp-2">{fieldValue}</span>;
        }
        return <span className="text-muted-foreground truncate">{fieldValue}</span>;

      case 'types':
      case 'glossaryItem':
      case 'dialog':
        // Handle array/comma-separated values
        let displayValue = '';
        if (Array.isArray(fieldValue)) {
          displayValue = fieldValue.length > 0 ? `${fieldValue.length} item(s)` : 'None';
        } else if (typeof fieldValue === 'string' && fieldValue.trim()) {
          const items = fieldValue.split(',').filter(Boolean);
          displayValue = items.length > 0 ? `${items.length} item(s)` : 'None';
        } else {
          displayValue = 'None';
        }
        return <span className="text-muted-foreground">{displayValue}</span>;

      case 'image':
      case 'file':
        if (fieldValue && typeof fieldValue === 'object' && fieldValue.url) {
          return (
            <span className="text-muted-foreground truncate inline-flex items-center gap-1">
              <IconProvider iconstring={fieldType === 'image' ? 'fa-image' : 'fa-file'} size={10} />
              {fieldValue.name || 'File attached'}
            </span>
          );
        }
        return <span className="text-muted-foreground">Attached</span>;

      default:
        // Handle any other type as string
        if (typeof fieldValue === 'object') {
          return <span className="text-muted-foreground truncate">{JSON.stringify(fieldValue)}</span>;
        }
        return <span className="text-muted-foreground truncate">{String(fieldValue)}</span>;
    }
  };

  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="font-medium min-w-[80px] truncate">{fieldLabel}:</span>
      <span className="flex-1 min-w-0">{renderValue()}</span>
    </div>
  );
};

